import { useContext, useEffect } from "react";
import Textarea from "react-textarea-autosize";
import { EditorContainerContext, getTitleMaxWidth, HEIGHT_PADDING } from "./EditorContainer/EditorContainerContext";

let canvas = document.createElement("canvas");

const resize = (titleEditorRef, setMaxTitleWidth, titleMaxWidthOffset = 0) => {
  if (!titleEditorRef.current) return;
  const minWidth = 50;
  const refValue = titleEditorRef.current;
  const newMaxWidth = getTitleMaxWidth(titleEditorRef, titleMaxWidthOffset);
  if (!newMaxWidth) return;

  // build one canvas for text measuring
  const ctx = canvas.getContext("2d");
  ctx.font = getComputedStyle(refValue).font || "16px sans-serif";

  const text = refValue.value || refValue.placeholder || "";
  const textMetrics = ctx.measureText(text);
  const w = Math.min(Math.max(textMetrics.width + 4, minWidth), newMaxWidth);

  // Calculate height based on canvas text metrics
  const glyphHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent + HEIGHT_PADDING;

  // Set width and white-space first so we can measure scrollHeight
  refValue.style.width = `${w}px`;
  refValue.style.whiteSpace = textMetrics.width < newMaxWidth ? "nowrap" : "pre-wrap";

  // Use scrollHeight for multiline text, glyph height for single line
  const isSingleLine = textMetrics.width < newMaxWidth && !text.includes("\n");
  const h = isSingleLine ? glyphHeight : refValue.scrollHeight;

  refValue.style.height = `${h}px`;
  // For single-line, set line-height equal to height for optical centering
  refValue.style.lineHeight = isSingleLine ? `${h}px` : "";
  setMaxTitleWidth(newMaxWidth);
};

export function AutoExpandTextarea(props) {
  const { titleEditorRef, maxTitleWidth, getTextHeight, setMaxTitleWidth, setTitle, titleMaxWidthOffset } = useContext(EditorContainerContext);
  const fontFamily = props.style?.fontFamily;

  // Resizes when the window resizes
  useEffect(() => {
    const resizeFn = () => {
      if (!titleEditorRef.current) return;
      resize(titleEditorRef, setMaxTitleWidth, titleMaxWidthOffset);
      setTitle(titleEditorRef.current.value);
      // Recompute glyph height/offset for vertical centering
      if (typeof getTextHeight === "function") {
        getTextHeight();
      }
    };

    // Ensure we measure after layout/font load to avoid initial miscalc on refresh
    const runInitial = () => {
      // Queue twice to be safe after layout
      requestAnimationFrame(() => requestAnimationFrame(resizeFn));
    };

    // If a specific font family is requested, try to load it before measuring
    if (document.fonts) {
      const loadPromise = fontFamily ? document.fonts.load(`1rem ${fontFamily}`) : document.fonts.ready;
      loadPromise.then(runInitial).catch(runInitial);
    } else {
      runInitial();
    }

    window.addEventListener("resize", resizeFn);

    // Observe the textarea itself for size changes (e.g., font changes affecting metrics)
    let ro;
    if (titleEditorRef.current && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(resizeFn);
      ro.observe(titleEditorRef.current);
    }
    return () => {
      window.removeEventListener("resize", resizeFn);
      if (ro) {
        try {
          ro.disconnect();
        } catch {}
      }
    };
  }, [titleEditorRef, props.value, fontFamily, setMaxTitleWidth, maxTitleWidth, setTitle, getTextHeight, titleMaxWidthOffset]);

  const handleOnChange = (event) => {
    if (!titleEditorRef.current) return;

    resize(titleEditorRef, setMaxTitleWidth, titleMaxWidthOffset);
    setTitle(titleEditorRef.current.value);
    if (typeof getTextHeight === "function") {
      getTextHeight();
    }
    props.onChange(event);
  };

  return (
    <div>
      <Textarea
        ref={titleEditorRef}
        wrap="off"
        type={props.type}
        className={props.className}
        aria-label={props.ariaLabel}
        value={props.value}
        onChange={handleOnChange}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        onKeyDown={props.onKeyDown}
        onClick={props.onClick}
        style={{
          ...props.style,
          minWidth: 50,
          maxWidth: maxTitleWidth,
          overflow: "hidden",
          resize: "none",
          boxSizing: "border-box",
        }}
      />
    </div>
  );
}
